import type { HoursServiceType } from '@/composables/useOperatingHours'

type Stringable = string | number

export function endpoints() {
  return {
    spotInfo: (spotId: Stringable) => `${spotId}/webapp/spot_info`,
    inquiriesList: (spotId: Stringable) => `${spotId}/webapp/inquiries`,
    createInquiry: (spotId: Stringable) => `${spotId}/webapp/inquiries`,
    updateInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/put_inquiries/${inquiryId}`,
    deleteInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}`,
    getInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}`,
    getProposal: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/proposal_for_inquiry/${inquiryId}`,
    createProposal: (spotId: Stringable) =>
      `${spotId}/webapp/create_proposal_for_inquiry`,
    editProposal: (spotId: Stringable, proposalPayment: Stringable) =>
      `${spotId}/webapp/update_proposal_for_inquiry/${proposalPayment}`,
    createProposalAttachment: (spotId: Stringable) =>
      `${spotId}/webapp/attachments`,
    refundPayment: (spotId: Stringable, paymentID: Stringable) =>
      `${spotId}/webapp/refund_payment/${paymentID}`,
    capturePayment: (spotId: Stringable, paymentID: Stringable) =>
      `${spotId}/webapp/capture_payment/${paymentID}`,
    getInquiryComments: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/`,
    createInquiryComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/`,
    getStandardInquiryAutoResponses: (
      spotId: Stringable,
      type: string,
      inquiryId: Stringable,
    ) =>
      `${spotId}/webapp/standard_inquiry_responses/${type}?inquiry_id=${inquiryId}`,
    getAutoResponseComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/autoresponse_comment/${inquiryId}`,
    cancelAutoResponse: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/cancel_autoresponse/${inquiryId}`,
    markCommentAsSeen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/see`,
    markCommentAsUnseen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/unsee`,
    getInquiryAttachments: (spotId: Stringable) =>
      `${spotId}/webapp/attachments`,
    getReusableAttachments: (spotId: Stringable) =>
      `${spotId}/webapp/reusable_attachments_list`,
    createInquiryAttachment: (spotId: Stringable) =>
      `${spotId}/webapp/create_attachment_for_inquiry_comment`,
    deleteAttachment: (spotId: Stringable, attachment_id: Stringable) =>
      `${spotId}/webapp/attachments/${attachment_id}`,
    cancelDelivery: (spotId: Stringable, orderId: Stringable) =>
      `${spotId}/webapp/cancel_delivery/${orderId}`,
    getDelivery: (spotId: Stringable, orderId: Stringable) =>
      `${spotId}/webapp/delivery/${orderId}`,
    toggleSpotNotifications: (spotId: Stringable) =>
      `${spotId}/webapp/toggle_notifications`,
    deleteCookies: (spotId: Stringable) => `${spotId}/webapp/cookies`,
    mixpanel: (spotId: Stringable) => `${spotId}/webapp`,
    getAppNotifications: (spotId: Stringable) =>
      `${spotId}/webapp/app_notification_settings`,
    pandadocTokens: (spotId: Stringable) => `${spotId}/webapp/pandadoc_token`,
    aiCoordinatorToken: (spotId: Stringable) => `${spotId}/webapp/ai_coordinator_token`,
    customerFeedback: (spotId: Stringable) => `${spotId}/webapp/customer_feedback`,
    putCustomerFeedback: (spotId: Stringable) => `${spotId}/webapp/put_customer_feedback`,
    setRequestedAppleReviewDate: (spotId: Stringable) => `${spotId}/webapp/set_requested_apple_review_date`,
  } as const
}

export function calendarEndpoints() {
  return {
    getCalendarEntries: (spotId: Stringable) => `${spotId}/calendar_v2/entries`,
    getCalendarPromotions: (spotId: Stringable) =>
      `${spotId}/calendar_v2/promotions`,
    getCalendarBlockedDays: (spotId: Stringable) =>
      `${spotId}/calendar_v2/blockers`,
    getCalendarSpotInfo: (spotId: Stringable) => `${spotId}/calendar_v2/info`,
    getCalendarFlyerCaptions: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/caption_idea/${id}`,
    getCalendarPostWorthyCaptions: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/pwi_post_caption_idea/${id}`,
    getCalendarGalleryPhotos: (spotId: Stringable) =>
      `${spotId}/calendar_v2/gallery`,
    calendarUploadGalleryPhoto: (spotId: Stringable) =>
      `${spotId}/calendar_v2/gallery`,
    calendarApprovePost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/approve/${id}`,
    calendarPostNow: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/post_now/${id}`,
    calendarEditPost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/item/${id}`,
    calendarDeleteEntry: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/item/${id}`,
    calendarChangeTextFragment: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/change_text_fragment/${id}`,
    calendarMixPanel: (spotId: Stringable) => `${spotId}/calendar_v2/mixpanel`,
    getCalendarActiveHolidayPromotions: (spotId: Stringable) =>
      `${spotId}/calendar_v2/active_holiday_promotions`,
    updateCalendarSpotInfo: (spotId: Stringable) =>
      `${spotId}/calendar_v2/info`,
    viewPost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/view_post/${id}`,
    calendarMediaEditorQueue: (spotId: Stringable) => `${spotId}/media_editor`,
    calendarMediaEditorRetryContentEdit: (spotId: Stringable, id: Stringable) =>
      `${spotId}/media_editor/retry_content_edit/${id}`,
    calendarMediaEditorCancelContentEdit: (
      spotId: Stringable,
      id: Stringable,
    ) => `${spotId}/media_editor/cancel_content_edit/${id}`,
  } as const
}

export function desktopEndpoints() {
  return {
    getInquiryComments: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/`,
    getInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}`,
    markCommentAsSeen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/see`,
    markCommentAsUnseen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/unsee`,
    getStandardInquiryAutoResponses: (
      spotId: Stringable,
      type: string,
      inquiryId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/standard_inquiry_responses/${type}?inquiry_id=${inquiryId}`,
    getAutoResponseComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/autoresponse_comment/${inquiryId}`,
    cancelAutoResponse: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/cancel_autoresponse/${inquiryId}`,
    pandadocTokens: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/pandadoc_token`,
    getInquiryAttachments: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/attachments`,
    getReusableAttachments: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/reusable_attachments_list`,
    createInquiryAttachment: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/create_attachment_for_inquiry_comment`,
    getAiResponses: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/ai_responses/${inquiryId}`,
    createInquiryComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/`,
    updateInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/put_inquiries/${inquiryId}`,
    deleteInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}`,
    createProposal: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/create_proposal_for_inquiry`,
    editProposal: (spotId: Stringable, proposalPayment: Stringable) =>
      `${spotId}/inquiry_manager/update_proposal_for_inquiry/${proposalPayment}`,
    getProposal: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/proposal_for_inquiry/${inquiryId}`,
    createProposalAttachment: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/attachments`,
    enableAutoConfirm: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/enable_auto_confirm`,
    enableReservationReminder: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/enable_reservation_reminder`,
    inquiriesList: (spotId: Stringable) => `${spotId}/inquiry_manager/inquiries`,
    seeInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/see_inquiries`,
    unseeInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/unsee_inquiries`,
    archiveInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/archive_inquiries`,
    mixpanel: (spotId: Stringable) => `${spotId}/inquiry_manager`,
    createAiCoordinatorSettings: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings`,
    getAiCoordinatorSettings: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings`,
    updateAiCoordinatorSettings: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings`,
    updateAiCoordinatorDocuments: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings_documents`,
    presignAiCoordinatorDocument: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings_presign_document`,
    presignAiCoordinatorDocumentDownload: (spotId: Stringable, storageKey: string) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings_presign_download?storage_key=${encodeURIComponent(storageKey)}`,
    pauseAiCoordinator: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings_pause`,
    resumeAiCoordinator: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/ai_coordinator_settings_resume`,
    updateInquiryAiHandling: (spotId: Stringable, inquiryId: Stringable, pausedAt?: string | null, forced?: boolean) => {
      const query = new URLSearchParams()
      if (pausedAt) query.set('ai_coordinator_paused_at', pausedAt)
      if (forced) query.set('forced', 'true')
      const queryString = query.toString()
      return `${spotId}/inquiry_manager/update_inquiry_ai_handling/${inquiryId}${queryString ? `?${queryString}` : ''}`
    },
    jobsFilePresign: (spotId: Stringable, storageKey: string, fileName: string) =>
      `${spotId}/inquiry_manager/jobs_resume_presign_download?storage_key=${encodeURIComponent(storageKey)}&file_name=${fileName}`,
    }
}

export function aiCoordinatorEndpoints() {
  return {
    getAiCoordinatorSettings: (spotId: Stringable) =>
      `user_api/ai_coordinator_settings?spot_id=${spotId}`,
    getAllCoordinatorSettings: (spotId: Stringable) =>
      `user_api/all_coordinator_settings?spot_id=${spotId}`,
    updateAiCoordinatorSettings: () => 'user_api/ai_coordinator_settings',
    updateAiCoordinatorDocuments: () => 'user_api/ai_coordinator_settings_documents',
    presignAiCoordinatorDocument: () => 'user_api/ai_coordinator_settings_presign_document',
    presignAiCoordinatorDocumentDownload: (spotId: Stringable, storageKey: string) =>
      `user_api/ai_coordinator_settings_presign_download?spot_id=${spotId}&storage_key=${encodeURIComponent(storageKey)}`,
    pauseAiCoordinator: () => 'user_api/ai_coordinator_settings_pause',
    resumeAiCoordinator: () => 'user_api/ai_coordinator_settings_resume',
    updateInquiryAiHandling: (inquiryId: Stringable) =>
      `user_api/update_inquiry_ai_handling/${inquiryId}`,
    getInquiry: () => 'eligibility/inquiry',
    inquiryHandlingPreview: () => 'user_api/inquiry_handling_preview',
    canForceHandling: () => 'eligibility/can_force_handling',
    canSwitchHandling: () => 'eligibility/can_switch_handling',
    refreshAiCoordinatorToken: () => 'auth/refresh_token',
  } as const
}

export function hoursEndpoints() {
  return {
    getoperationHours: (spotId: Stringable) =>
      `${spotId}/advanced_hours/hours_of_operation`,
    updateOperationHours: (spotId: Stringable) =>
      `${spotId}/advanced_hours/hours_of_operation`,
    getBlockedDays: (spotId: Stringable) =>
      `${spotId}/advanced_hours/blocked_days`,
    createBlockedDay: (spotId: Stringable) =>
      `${spotId}/advanced_hours/blocked_days`,
    updateBlockedDay: (spotId: Stringable) =>
      `${spotId}/advanced_hours/blocked_days`,
    deleteBlockedDay: (spotId: Stringable) =>
      `${spotId}/advanced_hours/blocked_days`,
    updateDescriptiveHours: (spotId: Stringable) =>
      `${spotId}/advanced_hours/descriptive_hours`,
    getAdditionalServiceHours: (spotId: Stringable, type: HoursServiceType) =>
      `${spotId}/advanced_hours/additional_service_hours/${type}`,
    updateAdditionalServiceHours: (spotId: Stringable, type: HoursServiceType) =>
      `${spotId}/advanced_hours/additional_service_hours/${type}`,
    getServiceHours: (spotId: Stringable) =>
      `${spotId}/advanced_hours/all`,
    updateServiceHours: (spotId: Stringable, serviceType: string) =>
      `${spotId}/advanced_hours/service_hours/${serviceType}`,
    getMenus: (spotId: Stringable) =>
      `${spotId}/advanced_hours/get_all_menus`,
    getTimetables: (spotId: Stringable) =>
      `${spotId}/advanced_hours/timetables`,
    createTimetable: (spotId: Stringable) =>
      `${spotId}/advanced_hours/timetables`,
    updateTimetable: (spotId: Stringable, timetableId: Stringable) =>
      `${spotId}/advanced_hours/timetables/${timetableId}`,
    deleteTimetable: (spotId: Stringable, timetableId: Stringable) =>
      `${spotId}/advanced_hours/timetables/${timetableId}`,
    assignMenuToTimetable: (spotId: Stringable) =>
      `${spotId}/advanced_hours/assign_menu_to_timetable`,
  }
}

export function pricingEndpoints() {
  return {
    getPricingPackages: (spotId: Stringable) => `${spotId}/pricing_page`,
    swapPricingFeature: (spotId: Stringable) => `${spotId}/pricing_page/swap`,
    upgradePricingFeature: (spotId: Stringable) =>
      `${spotId}/pricing_page/upgrade`,
  } as const
}

export function ccpaEndpoints() {
  return {
    settings: (spotId: Stringable) => `${spotId}/ccpa_settings`,
    document: (spotId: Stringable, type: string) =>
      `${spotId}/ccpa_settings/${type}`,
    uploadAdminFile: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/upload_admin_file`,
    uploadCustomerFile: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/upload_customer_file`,
    deleteAdminFile: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/delete_admin_file`,
    deleteCustomerFile: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/delete_customer_file`,
    downloadAdminFile: (spotId: Stringable, type: string) =>
      `${spotId}/ccpa_settings/download_admin_file?type=${type}`,
    downloadCustomerFile: (spotId: Stringable, type: string) =>
      `${spotId}/ccpa_settings/download_customer_file?type=${type}`,
    customerApproval: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/customer_approval`,
    resetCustomerApproval: (spotId: Stringable) =>
      `${spotId}/ccpa_settings/reset_customer_approval`,
  } as const
}

export function menuEndpoints() {
  return {
    getAllMenus: (spotId: Stringable) =>
      `${spotId}/webapp_menus/get_all_menus`,
    setMenuVisibility: (spotId: Stringable) =>
      `${spotId}/webapp_menus/set_menu_visibility`,
    createItem: (spotId: Stringable) => `${spotId}/webapp_menus/food_menu_item`,
    item: (spotId: Stringable, itemId: Stringable) =>
      `${spotId}/webapp_menus/food_menu_item/${itemId}`,
    setItemImages: (spotId: Stringable) =>
      `${spotId}/webapp_menus/set_item_images`,
    foodLabels: (spotId: Stringable) => `${spotId}/webapp_menus/food_labels`,
    createPrice: (spotId: Stringable) => `${spotId}/webapp_menus/food_price`,
    price: (spotId: Stringable, priceId: Stringable) =>
      `${spotId}/webapp_menus/food_price/${priceId}`,
    setPostsActive: (spotId: Stringable) =>
      `${spotId}/webapp_menus/set_posts_active`,
  }
}
